import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';

const COLORS = {
  USD: '#34d399', EUR: '#60a5fa', GBP: '#a78bfa', NOK: '#f472b6', KES: '#fbbf24',
  ETB: '#fb923c', SAR: '#2dd4bf', AED: '#38bdf8', USDT: '#22c55e', USDC: '#3b82f6', BTC: '#f59e0b',
};

const SEED = [
  { code: 'USD', sym: '$', type: 'fiat', rate: 1.0, change_24h: 0 },
  { code: 'EUR', sym: '€', type: 'fiat', rate: 0.92 },
  { code: 'GBP', sym: '£', type: 'fiat', rate: 0.79 },
  { code: 'NOK', sym: 'kr', type: 'fiat', rate: 10.72 },
  { code: 'KES', sym: 'KSh', type: 'fiat', rate: 129.4 },
  { code: 'ETB', sym: 'Br', type: 'fiat', rate: 57.2 },
  { code: 'SAR', sym: 'SR', type: 'fiat', rate: 3.75 },
  { code: 'AED', sym: 'AED', type: 'fiat', rate: 3.67 },
  { code: 'BTC', sym: '₿', type: 'crypto', price_usd: 67000, change_24h: 0, sparkline: [] },
  { code: 'USDT', sym: '₮', type: 'crypto', price_usd: 1.0, change_24h: 0, sparkline: [] },
  { code: 'USDC', sym: '$', type: 'crypto', price_usd: 1.0, change_24h: 0, sparkline: [] },
];

const fmtPrice = (v) =>
  v == null ? '—' : v >= 1000 ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : v >= 1 ? v.toFixed(2) : v.toPrecision(3);
const fmtRate = (v) =>
  v == null ? '—' : v >= 100 ? v.toFixed(1) : v >= 1 ? v.toFixed(2) : v.toFixed(3);

const Sparkline = ({ data, color }) => {
  if (!Array.isArray(data) || data.length < 2) return null;
  const w = 46, h = 16;
  const min = Math.min(...data), max = Math.max(...data);
  const range = max - min || 1;
  const pts = data
    .map((v, i) => `${((i / (data.length - 1)) * w).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={w} height={h} className="opacity-80" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

const GlobalCurrencyBanner = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(SEED);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get('/market/rates');
        if (active && Array.isArray(data?.rates)) {
          setRows(data.rates);
          setUpdatedAt(data.updated_at);
        }
      } catch (e) {
        /* keep seed data on failure */
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const onChipClick = (c) => {
    if (c.type === 'crypto') navigate('/crypto');
    else navigate(`/send?to=${c.code}`);
  };

  const track = [...rows, ...rows];
  const chipValue = (c) => (c.type === 'crypto' ? `$ ${fmtPrice(c.price_usd)}` : `${c.sym} ${fmtRate(c.rate)}`);

  return (
    <div
      data-testid="global-currency-banner"
      className="relative overflow-hidden border-b border-white/10"
      style={{ background: 'linear-gradient(120deg, hsl(222 47% 9%), hsl(222 47% 13%) 55%, hsl(215 40% 16%))' }}
    >
      <style>{`
        @keyframes ezyMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .ezy-track { animation: ezyMarquee 45s linear infinite; }
        .ezy-marquee:hover .ezy-track { animation-play-state: paused; }
        @keyframes ezyPulse { 0%,100% { opacity: .35; } 50% { opacity: .7; } }
      `}</style>

      <img
        src="/world-map.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none select-none absolute inset-0 w-full h-full object-cover opacity-35"
        style={{ animation: 'ezyPulse 6s ease-in-out infinite' }}
      />
      <div className="pointer-events-none absolute -left-16 top-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(52,211,153,0.28), transparent 70%)' }} />
      <div className="pointer-events-none absolute right-0 -bottom-10 w-72 h-72 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.20), transparent 70%)' }} />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-4">
        <div className="flex items-center gap-3 mb-3">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
          </span>
          <h2 className="text-white font-heading font-bold text-base md:text-lg tracking-tight">Global Settlement Network</h2>
          <span className="hidden md:inline text-white/50 text-sm">· Live FX &amp; crypto · tap to send</span>
          {updatedAt && (
            <span className="hidden lg:inline text-white/30 text-xs ml-auto" data-testid="ticker-updated">
              Updated {new Date(updatedAt).toLocaleTimeString()}
            </span>
          )}
        </div>

        <div className="ezy-marquee relative overflow-hidden" data-testid="currency-ticker">
          <div className="ezy-track flex w-max gap-3">
            {track.map((c, i) => {
              const color = COLORS[c.code] || '#94a3b8';
              const chg = c.change_24h;
              const hasChg = typeof chg === 'number' && c.code !== 'USD';
              const up = hasChg ? chg >= 0 : true;
              return (
                <button
                  type="button"
                  key={`${c.code}-${i}`}
                  onClick={() => onChipClick(c)}
                  data-testid={i < rows.length ? `currency-chip-${c.code}` : undefined}
                  className="flex items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 backdrop-blur-sm cursor-pointer transition-transform duration-200 hover:scale-105 hover:brightness-125 focus:outline-none focus:ring-2 focus:ring-emerald-400/60"
                  style={{ borderColor: `${color}55`, background: `${color}14` }}
                  title={`${c.name || c.code} — click to send`}
                >
                  <span className="font-bold text-sm" style={{ color }}>{c.code}</span>
                  <span className="text-white/80 text-sm font-mono">{chipValue(c)}</span>
                  {c.type === 'crypto' && <Sparkline data={c.sparkline} color={up ? '#34d399' : '#fb7185'} />}
                  {hasChg ? (
                    <span className={`text-[11px] font-semibold ${up ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {up ? '▲' : '▼'} {Math.abs(chg).toFixed(2)}%
                    </span>
                  ) : c.type === 'fiat' && c.code !== 'USD' ? (
                    <span className="text-[10px] text-white/40">/$1</span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-16" style={{ background: 'linear-gradient(90deg, hsl(222 47% 11%), transparent)' }} />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-16" style={{ background: 'linear-gradient(270deg, hsl(222 47% 11%), transparent)' }} />
        </div>
      </div>
    </div>
  );
};

export default GlobalCurrencyBanner;
